import { useSyncExternalStore } from 'react';

type FeatureFlagKey =
  | '__CANVAS_ACCESS_ENABLED__'
  | '__FEATURE_ADVANCED__'
  | '__FEATURE_DEV_TOOLS__';

const FEATURE_FLAG_KEYS: FeatureFlagKey[] = [
  '__CANVAS_ACCESS_ENABLED__',
  '__FEATURE_ADVANCED__',
  '__FEATURE_DEV_TOOLS__',
];

const EVENT_NAME = 'feature-flag-change';

/** 内部存储，defineProperty 的 getter/setter 读写此对象 */
const flagStore: Record<string, boolean> = {};

/**
 * 将 window.__FEATURE_*__ 改为 getter/setter，
 * 控制台直接 `window.__CANVAS_ACCESS_ENABLED__ = true` 即可触发组件重渲染。
 */
export function installFeatureFlagTraps() {
  for (const key of FEATURE_FLAG_KEYS) {
    // 先把当前已有的值搬到 store 里（index.tsx 初始化时可能已经赋过值）
    flagStore[key] = !!window[key];

    Object.defineProperty(window, key, {
      configurable: true,
      enumerable: true,
      get: () => flagStore[key],
      set: (value: unknown) => {
        flagStore[key] = !!value;
        window.dispatchEvent(new Event(EVENT_NAME));
      },
    });
  }
}

/**
 * 响应式读取 feature flag（布尔类型）。
 * 控制台直接 `window.__CANVAS_ACCESS_ENABLED__ = true` 后组件自动重渲染。
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useSyncExternalStore(
    (onStoreChange) => {
      window.addEventListener(EVENT_NAME, onStoreChange);
      return () => window.removeEventListener(EVENT_NAME, onStoreChange);
    },
    () => flagStore[key] === true,
    () => false,
  );
}

export function useCanvasAccessAllowed(): boolean {
  const isCanvasEnabled = useFeatureFlag('__CANVAS_ACCESS_ENABLED__');
  const isLocalEnvironment = typeof __ENV_TYPE__ !== 'undefined' && __ENV_TYPE__ === 'local';
  return isLocalEnvironment || isCanvasEnabled;
}
